import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DependencyService } from './dependencyService ';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { selectRequiredValidator } from '../../../validators/custom-validators';

@Component({
  selector: 'app-order-flow-model-choice',
  templateUrl: './order-flow-model-choice.component.html',
  styleUrl: './order-flow-model-choice.component.css',
  imports: [CommonModule, ReactiveFormsModule],
  standalone: true,
})
export class OrderFlowModelChoiceComponent implements AfterViewInit {
  @Input() models: any[] = [];
  @Input() attributes: any[] = [];
  @Input() options: any[] = [];
  @Input() selectedModelFromDuplicate: number | null = null;
  @Output() progressUpdated: EventEmitter<number> = new EventEmitter<number>();

  filteredAttributes: any[] = [];
  filteredOptions: any[] = [];
  configurationForm!: FormGroup;
  selectedValues: { [key: number]: number } = {};
  selectedOptionValues: { [key: number]: number } = {};
  selectedModelId: number | null = null;
  textValues: { [attributeGuid: string]: string | undefined } = {};
  isBothSelected: boolean = false;
  objectKeys = Object.keys;

  touchedFields: string[] = [];
  // Track the selected attribute options to send in the payload
  attributeOptions: { attributeOptionId: number; valueId: number, value: string | undefined }[] = [];

  constructor(private fb: FormBuilder, private dependencyService: DependencyService) { }
  ngAfterViewInit(): void {
    if (this.selectedModelFromDuplicate) {

      this.populateModelSelection(this.selectedModelFromDuplicate);
      // After populating the model selection, attributeOptions will be populated within the method
    } else {
      this.dependencyService.updateVisibility(this.attributes, this.options, this.selectedValues, this.selectedOptionValues);
    }

    this.isBothSelected = false;
  }


 // Adicione também no método initilizeOptionAndAttributes após criar o form:
initilizeOptionAndAttributes(modelId: number): void {
  // Filter attributes and options by modelId
  this.filteredAttributes = this.attributes.filter(attr => attr.modelId === modelId && attr.isVisible);
  this.filteredOptions = this.options.filter(opt => opt.modelId === modelId && opt.isVisible);

  const sortValues = (values: any[]) => {
    values.sort((a, b) => {
      const valA = a.value?.toString().toLowerCase() || '';
      const valB = b.value?.toString().toLowerCase() || '';

      const isNoA = valA === 'no';
      const isNoB = valB === 'no';

      if (isNoA && !isNoB) return -1;
      if (!isNoA && isNoB) return 1;
      if (isNoA && isNoB) return 0;

      const numA = parseFloat(valA);
      const numB = parseFloat(valB);

      const isNumA = !isNaN(numA);
      const isNumB = !isNaN(numB);

      if (isNumA && isNumB) return numA - numB;
      if (isNumA && !isNumB) return -1;
      if (!isNumA && isNumB) return 1;

      return valA.localeCompare(valB);
    });
  };

  this.filteredOptions.forEach(opt => {
    if (Array.isArray(opt.values)) {
      sortValues(opt.values);
    }
  });

  this.filteredAttributes.forEach(attr => {
    if (Array.isArray(attr.values)) {
      sortValues(attr.values);
    }
  });

  this.createForm();
  // Clear previous selections
  this.attributeOptions = [];

  // Update visibility based on the selected model
  this.dependencyService.updateVisibility(this.filteredAttributes, this.filteredOptions, this.selectedValues, this.selectedOptionValues);

  // Populate attributeOptions based on the filteredAttributes
  this.populateAttributeOptions();
  
  // IMPORTANTE: Atualizar validadores após criar o form e popular os atributos
  this.updateFormValidators();

  // Initialize progress based on the form's current state
  this.calculateAndUpdateProgress();
}


  /**
 * Populates the attributeOptions array based on the selected values in filteredAttributes.
 * This method bypasses the onSelectValue event to avoid triggering additional side effects.
 */
populateAttributeOptions(): void {
  this.attributeOptions = [];

  // Attributes
  this.filteredAttributes.forEach(attribute => {
    if (!(attribute.isEnable && !attribute.isHidden)) return;

    const ctrl = this.configurationForm.get(attribute.attributeOptionId.toString());

    if (attribute.componentType === 'CheckBox') {
      attribute.values.forEach((v: any) => {
        if (v.selectedValue || v.defaultValue) {
          this.attributeOptions.push({
            attributeOptionId: attribute.attributeOptionId,
            valueId: v.valueId,
            value: undefined
          });
        }
      });
      // keep form control synced
      const selected = attribute.values
        .filter((v: any) => v.selectedValue || v.defaultValue)
        .map((v: any) => v.valueId);
      ctrl?.setValue(selected, { emitEvent: false });

    } else if (attribute.componentType === 'Text') {
      const text = attribute.selectedValue ?? ''; // or attribute.defaultValue if you have it
      if (text !== '') {
        this.attributeOptions.push({
          attributeOptionId: attribute.attributeOptionId,
          valueId: 0,
          value: text
        });
      }
      ctrl?.setValue(text, { emitEvent: false });

    } else {
      const valueId =
        attribute.values.find((v: any) => v.selectedValue)?.valueId ??
        attribute.values.find((v: any) => v.defaultValue)?.valueId;

      if (valueId !== undefined && valueId !== null) {
        this.attributeOptions.push({
          attributeOptionId: attribute.attributeOptionId,
          valueId,
          value: undefined
        });
        ctrl?.setValue(valueId, { emitEvent: false });
      }
    }
  });

  // Options (same idea)
  this.filteredOptions.forEach(option => {
    if (!(option.isEnable && !option.isHidden)) return;

    const ctrl = this.configurationForm.get(option.attributeOptionId.toString());

    if (option.componentType === 'CheckBox') {
      option.values.forEach((v: any) => {
        if (v.selectedValue || v.defaultValue) {
          this.attributeOptions.push({
            attributeOptionId: option.attributeOptionId,
            valueId: v.valueId,
            value: undefined
          });
        }
      });
      const selected = option.values
        .filter((v: any) => v.selectedValue || v.defaultValue)
        .map((v: any) => v.valueId);
      ctrl?.setValue(selected, { emitEvent: false });

    } else if (option.componentType === 'Text') {
      const text = option.selectedValue ?? '';
      if (text !== '') {
        this.attributeOptions.push({
          attributeOptionId: option.attributeOptionId,
          valueId: 0,
          value: text
        });
      }
      ctrl?.setValue(text, { emitEvent: false });

    } else {
      const valueId =
        option.values.find((v: any) => v.selectedValue)?.valueId ??
        option.values.find((v: any) => v.defaultValue)?.valueId;

      if (valueId !== undefined && valueId !== null) {
        this.attributeOptions.push({
          attributeOptionId: option.attributeOptionId,
          valueId,
          value: undefined
        });
        ctrl?.setValue(valueId, { emitEvent: false });
      }
    }
  });

  this.calculateAndUpdateProgress();
}

private rebuildAttributeOptions(): void {

  const formValues = this.configurationForm.value;

  this.attributeOptions = Object.keys(formValues)
    .filter(key => formValues[key] !== null && formValues[key] !== -1)
    .flatMap(key => {

      const value = formValues[key];

      if (Array.isArray(value)) {
        return value.map(v => ({
          attributeOptionId: Number(key),
          valueId: v,
          value: undefined
        }));
      }

      return [{
        attributeOptionId: Number(key),
        valueId: value,
        value: undefined
      }];

    });

}

onCheckboxChange(attributeId: number, valueId: number, event: Event): void {
  const target = event.target as HTMLInputElement;
  const isChecked = target.checked;
  
  // Atualizar o attributeOptions array
  if (isChecked) {
    // Adicionar se marcado
    this.attributeOptions.push({
      attributeOptionId: attributeId,
      valueId: valueId,
      value: undefined
    });
  } else {
    // Remover se desmarcado
    const index = this.attributeOptions.findIndex(
      option => option.attributeOptionId === attributeId && option.valueId === valueId
    );
    if (index > -1) {
      this.attributeOptions.splice(index, 1);
    }
  }

  // Atualizar o FormControl com o array de valores selecionados
  const selectedValues = this.attributeOptions
    .filter(option => option.attributeOptionId === attributeId)
    .map(option => option.valueId);
  
  const control = this.configurationForm.get(attributeId.toString());
  if (control) {
    control.setValue(selectedValues);
  }

  // Atualizar visibilidade e progresso
  this.dependencyService.updateVisibility(
    this.filteredAttributes, 
    this.filteredOptions, 
    this.selectedValues, 
    this.selectedOptionValues,
    this.attributeOptions
  );
  
  // IMPORTANTE: Atualizar validadores após mudanças
  this.updateFormValidators();
  
  this.calculateAndUpdateProgress();

}

  onSelectModel(modelId: number): void {
    this.selectedModelId = modelId;
    this.isBothSelected = false;

    this.initilizeOptionAndAttributes(modelId);
  }

 onSelectValue(attributeId: number, valueId: number): void {
  // Get the previous value for this attribute
  const previousValue = this.selectedValues[attributeId];
  
  // Check if this is a value change (not just initial selection)
  const isValueChange = previousValue !== undefined && previousValue !== valueId;

  if (isNaN(valueId)) {
    valueId = 0;
  }
  
  // Set the new value
  this.selectedValues[attributeId] = valueId;
  
  // Update the form control
  if (this.configurationForm.contains(attributeId.toString())) {
    this.configurationForm.get(attributeId.toString())?.setValue(valueId);
  }
  
  // Find if the attribute option is already in the list
  const existingOptionIndex = this.attributeOptions.findIndex(option => option.attributeOptionId === attributeId);

  if (existingOptionIndex > -1) {
    // Update the existing option
    this.attributeOptions[existingOptionIndex].valueId = valueId;
  } else {
    // Add the new option
    this.attributeOptions.push({ attributeOptionId: attributeId, valueId: valueId, value: undefined });
  }

  // If we're changing a value (not just setting it for the first time), 
  // reset all dependent attributes
  if (isValueChange) {
    // For all dependent form controls, we need to reset them
    this.filteredAttributes.forEach(attribute => {
      if (this.isDependentOn(attribute.dependencies, attributeId) && 
          this.configurationForm.contains(attribute.attributeOptionId.toString())) {
        this.configurationForm.get(attribute.attributeOptionId.toString())?.reset();
      }
    });
    
    this.filteredOptions.forEach(option => {
      if (this.isDependentOn(option.dependencies, attributeId) && 
          this.configurationForm.contains(option.attributeOptionId.toString())) {
        this.configurationForm.get(option.attributeOptionId.toString())?.reset();
      }
    });
    
    // Reset dependent values and options
    this.dependencyService.resetCascadingDependencies(
      attributeId, 
      this.filteredAttributes, 
      this.filteredOptions, 
      this.selectedValues, 
      this.selectedOptionValues,
      this.attributeOptions
    );
  } else {
    // Update visibility based on selected values
    this.dependencyService.updateVisibility(
      this.filteredAttributes, 
      this.filteredOptions, 
      this.selectedValues, 
      this.selectedOptionValues,
      this.attributeOptions
    );
  }

  // IMPORTANTE: Atualizar validadores após mudanças de visibilidade
  this.updateFormValidators();

  // Recalculate and update progress
  this.calculateAndUpdateProgress();

}

  private isDependentOn(dependencies: any[], attributeId: number): boolean {
    if (!dependencies || dependencies.length === 0) {
      return false;
    }

    return dependencies.some(dep =>
      (dep.objectType === 'AttributeValue' || dep.objectType === 'Attribute') &&
      dep.attributeId === attributeId
    );
  }

  onTextValueChange(attributeOptionId: number, event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.textValues[attributeOptionId] = inputElement.value;

    const existingTextIndex = this.attributeOptions.findIndex(option => option.attributeOptionId === attributeOptionId);

    if (existingTextIndex > -1) {
      // Update the existing option
      this.attributeOptions[existingTextIndex].value = inputElement.value;
    } else {
      // Add the new option
      this.attributeOptions.push({ attributeOptionId: attributeOptionId, valueId: 0, value: inputElement.value });
    }

  }

 onSelectOptionValue(optionId: number, valueId: number): void {
  // Get the previous value for this option
  const previousValue = this.selectedOptionValues[optionId];
  
  // Check if this is a value change (not just initial selection)
  const isValueChange = previousValue !== undefined && previousValue !== valueId;
  
  // Set the new value
  this.selectedOptionValues[optionId] = valueId;
  
  // Update the form control
  if (this.configurationForm.contains(optionId.toString())) {
    this.configurationForm.get(optionId.toString())?.setValue(valueId);
  }

  // Find if the option is already in the list
  const existingOptionIndex = this.attributeOptions.findIndex(option => option.attributeOptionId === optionId);

  if (existingOptionIndex > -1) {
    // Update the existing option
    this.attributeOptions[existingOptionIndex].valueId = valueId;
  } else {
    // Add the new option
    this.attributeOptions.push({ attributeOptionId: optionId, valueId: valueId, value: undefined });
  }

  // If we're changing a value (not just setting it for the first time), 
  // reset all dependent options
  if (isValueChange) {
    // For all dependent form controls, we need to reset them
    this.filteredAttributes.forEach(attribute => {
      if (this.isDependentOnOption(attribute.dependencies, optionId) && 
          this.configurationForm.contains(attribute.attributeOptionId.toString())) {
        this.configurationForm.get(attribute.attributeOptionId.toString())?.reset();
      }
    });
    
    this.filteredOptions.forEach(option => {
      if (this.isDependentOnOption(option.dependencies, optionId) && 
          this.configurationForm.contains(option.attributeOptionId.toString())) {
        this.configurationForm.get(option.attributeOptionId.toString())?.reset();
      }
    });
    
    // Reset dependent values and options
    this.dependencyService.resetCascadingDependencies(
      optionId, 
      this.filteredAttributes, 
      this.filteredOptions, 
      this.selectedValues, 
      this.selectedOptionValues,
      this.attributeOptions
    );
  } else {
    // Update visibility based on selected option values
    this.dependencyService.updateVisibility(
      this.filteredAttributes, 
      this.filteredOptions, 
      this.selectedValues, 
      this.selectedOptionValues,
      this.attributeOptions
    );
  }

  // IMPORTANTE: Atualizar validadores após mudanças de visibilidade
  this.updateFormValidators();

  // Recalculate and update progress
  this.calculateAndUpdateProgress();

}

  private isDependentOnOption(dependencies: any[], optionId: number): boolean {
    if (!dependencies || dependencies.length === 0) {
      return false;
    }

    return dependencies.some(dep =>
      dep.objectType === 'OptionValue' && dep.attributeId === optionId
    );
  }



  resetAllDependentSelections(): void {
    // Reset all attribute selections that have dependencies
    this.filteredAttributes.forEach(attribute => {
      if (attribute.dependencies && attribute.dependencies.length > 0) {
        delete this.selectedValues[attribute.attributeOptionId];

        attribute.values.forEach((value: any) => {
          if (value.selectedValue) {
            value.selectedValue = false;
          }
        });

        if (attribute.componentType === 'Text') {
          attribute.selectedValue = '';
        }

        if (this.configurationForm.contains(attribute.attributeOptionId.toString())) {
          this.configurationForm.get(attribute.attributeOptionId.toString())?.reset();
        }

        // Remove from attributeOptions array
        const idx = this.attributeOptions.findIndex(opt => opt.attributeOptionId === attribute.attributeOptionId);
        if (idx > -1) {
          this.attributeOptions.splice(idx, 1);
        }
      }
    });

    // Reset all option selections that have dependencies
    this.filteredOptions.forEach(option => {
      if (option.dependencies && option.dependencies.length > 0) {
        delete this.selectedOptionValues[option.attributeOptionId];

        option.values.forEach((value: any) => {
          if (value.selectedValue) {
            value.selectedValue = false;
          }
        });

        if (option.componentType === 'Text') {
          option.selectedValue = '';
        }

        if (this.configurationForm.contains(option.attributeOptionId.toString())) {
          this.configurationForm.get(option.attributeOptionId.toString())?.reset();
        }

        // Remove from attributeOptions array
        const idx = this.attributeOptions.findIndex(opt => opt.attributeOptionId === option.attributeOptionId);
        if (idx > -1) {
          this.attributeOptions.splice(idx, 1);
        }
      }
    });

    // Update visibility for all items
    this.dependencyService.updateVisibility(
      this.filteredAttributes,
      this.filteredOptions,
      this.selectedValues,
      this.selectedOptionValues,
      this.attributeOptions
    );

    // Recalculate and update progress
    this.calculateAndUpdateProgress();
  }

  isFieldInvalid(attributeId: number): boolean {
    const control = this.configurationForm.get(attributeId.toString());
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  goBackToModelSelection(): void {
    this.selectedModelId = null;
    this.filteredAttributes = [];
    this.filteredOptions = [];

    this.updateProgress(30);
  }

  getSelectedModelName(): string {
    const selectedModel = this.models.find(model => model.modelId === this.selectedModelId);
    return selectedModel ? selectedModel.name : '';
  }

  /**
   * Populates the model selection with a pre-selected model.
   * @param modelId The model ID to select.
   */
  populateModelSelection(modelId: number): void {
    this.selectedModelId = modelId;

    this.initilizeOptionAndAttributes(modelId);

  }

  populateAttributes(selectedAttributes: { [key: number]: number }): void {
    this.selectedValues = selectedAttributes;
    Object.keys(selectedAttributes).forEach((attrId): void => {
      const numericAttrId = Number(attrId); // Convert to number
      if (!isNaN(numericAttrId)) { // Optional: Check if conversion is successful
        this.configurationForm.get(`attr_${numericAttrId}`)?.setValue(selectedAttributes[numericAttrId]);
      } else {
        console.warn(`Invalid attribute ID: ${attrId}`);
      }
    });

    // After setting selected values, populate attributeOptions
    this.populateAttributeOptions();
  }

  /**
   * Populates the options with pre-selected values.
   * @param selectedOptions An object mapping optionAttributeOptionId to valueId.
   */
  populateOptions(selectedOptions: { [key: number]: number }): void {
    this.selectedOptionValues = selectedOptions;

    Object.keys(selectedOptions).forEach((optIdStr): void => {
      const optId = Number(optIdStr); // Convert string to number

      if (isNaN(optId)) {
        console.warn(`Invalid option ID: ${optIdStr}`);
        return; // Skip invalid keys
      }

      const controlName = `opt_${optId}`;
      const control = this.configurationForm.get(controlName);

      if (control) {
        control.setValue(selectedOptions[optId]);
      } else {
        console.warn(`Form control '${controlName}' not found.`);
      }
    });
  }

  /**
  * Resets all selections.
  */
  resetSelection(): void {
    this.configurationForm.reset();
    this.selectedModelId = null;
    this.selectedValues = {};
    this.selectedOptionValues = {};
  }

createForm() {
  const group: any = {};

  this.filteredAttributes.forEach((attribute) => {
    if (attribute.componentType === 'CheckBox') {
      // Para checkboxes, crie um array com os valores selecionados
      const selectedValues = attribute.values
        .filter((value: any) => value.selectedValue || value.defaultValue)
        .map((value: any) => value.valueId);

      group[attribute.attributeOptionId] = [
        selectedValues,
        attribute.isRequired && attribute.isEnable && !attribute.isHidden ? Validators.required : Validators.nullValidator,
      ];
    } else {
      let selectedValue = attribute.values.find((value: any) => value.selectedValue)?.valueId;

      if (selectedValue === undefined || selectedValue === null) {
        selectedValue = attribute.values.find((value: any) => value.defaultValue)?.valueId;
      }

      if (attribute.componentType === 'Text' && (selectedValue === undefined || selectedValue === null)) {
        selectedValue = '';
      }

      if (selectedValue === undefined || selectedValue === null) {
        selectedValue = -1;
      }

      group[attribute.attributeOptionId] = [
        selectedValue,
        attribute.isRequired && attribute.isEnable && !attribute.isHidden ? selectRequiredValidator() : Validators.nullValidator,
      ];

      if (selectedValue !== '' && selectedValue !== -1) {
        this.selectedValues[attribute.attributeOptionId] = selectedValue;
      }
    }
  });

  this.filteredOptions.forEach((option) => {
    if (option.componentType === 'CheckBox') {
      const selectedValues = option.values
        .filter((value: any) => value.selectedValue || value.defaultValue)
        .map((value: any) => value.valueId);

      group[option.attributeOptionId] = [
        selectedValues,
        option.isRequired && option.isEnable && !option.isHidden ? Validators.required : Validators.nullValidator,
      ];
    } else {
      let selectedValue = option.values.find((value: any) => value.selectedValue)?.valueId;

      // Fallback to defaultValue if no selectedValue found
      if (selectedValue === undefined || selectedValue === null) {
        selectedValue = option.values.find((value: any) => value.defaultValue)?.valueId;
      }

      // If this option is a text field and no selected value found, default to empty string
      if (option.componentType === 'Text' && (selectedValue === undefined || selectedValue === null)) {
        selectedValue = '';
      }

      // Final fallback
      if (selectedValue === undefined || selectedValue === null) {
        selectedValue = -1;
      }

      group[option.attributeOptionId] = [
        selectedValue,
        option.isRequired && option.isEnable && !option.isHidden ? selectRequiredValidator() : Validators.nullValidator,
      ];

      // Set the selectedOptionValues to update the state
      if (selectedValue !== '' && selectedValue !== -1) {
        this.selectedOptionValues[option.attributeOptionId] = selectedValue;
      }
    }
  });

  this.configurationForm = this.fb.group(group);

  Object.keys(this.configurationForm.controls).forEach((controlName) => {
  const control = this.configurationForm.get(controlName);

    control?.valueChanges.subscribe(() => {
      if (!control.dirty) return;

      const attribute = this.filteredAttributes.find(
        x => x.attributeOptionId.toString() === controlName
      );

      const option = this.filteredOptions.find(
        x => x.attributeOptionId.toString() === controlName
      );

      const fieldName =
        attribute?.name ??
        option?.name ??
        controlName;

      if (!this.touchedFields.includes(fieldName)) {
        this.touchedFields.push(fieldName);
      }
    });
  });

  // Update visibility after setting selected values
  this.dependencyService.updateVisibility(this.filteredAttributes, this.filteredOptions, this.selectedValues, this.selectedOptionValues);

  // Subscribe to form value changes to update progress
  this.configurationForm.valueChanges.subscribe(() => {
    this.calculateAndUpdateProgress();
  });
}

  
  /**
 * Atualiza os validadores dos form controls baseado na visibilidade e estado dos campos
 */
  private isEmptySelection(value: any, componentType: string): boolean {
    if (componentType === 'CheckBox') return !Array.isArray(value) || value.length === 0;
    if (componentType === 'Text')     return value === null || value === undefined || value === '';
    return value === null || value === undefined || value === -1;
  }

  private defaultFor(meta: any): any {

    if (meta.componentType === 'CheckBox') {
      return (meta.values || [])
        .filter((v: any) => v.defaultValue || v.selectedValue)
        .map((v: any) => v.valueId);
    }
    if (meta.componentType === 'Text') {
      return meta.selectedValue ?? '';
    }

    const v =
      meta.values?.find((v: any) => v.defaultValue)?.valueId ??
      meta.values?.find((v: any) => v.selectedValue)?.valueId;
    return v ?? -1;
  }

  private emptyFor(componentType: string): any {
    if (componentType === 'CheckBox') return [];
    if (componentType === 'Text')     return '';
    return -1; 
  }

  updateFormValidators(): void {
    if (!this.configurationForm) return;

    this.filteredAttributes.forEach((attribute) => {
      const control = this.configurationForm.get(attribute.attributeOptionId.toString());
      if (!control) return;

      if (attribute.isRequired && attribute.isEnable && !attribute.isHidden) {
        control.setValidators(
          attribute.componentType === 'CheckBox' ? [Validators.required] : [selectRequiredValidator()]
        );
      } else {
        control.clearValidators();
      }

      if (attribute.isEnable && !attribute.isHidden) {
        if (this.isEmptySelection(control.value, attribute.componentType)) {
          control.setValue(this.defaultFor(attribute), { emitEvent: false });
        }
      } else {
        control.setValue(this.emptyFor(attribute.componentType), { emitEvent: false });
      }

      control.updateValueAndValidity({ emitEvent: false });
    });

    this.filteredOptions.forEach((option) => {
      const control = this.configurationForm.get(option.attributeOptionId.toString());
      if (!control) return;

      if (option.isRequired && option.isEnable && !option.isHidden) {
        control.setValidators(
          option.componentType === 'CheckBox' ? [Validators.required] : [selectRequiredValidator()]
        );
      } else {
        control.clearValidators();
      }

      if (option.isEnable && !option.isHidden) {
        if (this.isEmptySelection(control.value, option.componentType)) {
          control.setValue(this.defaultFor(option), { emitEvent: false });
        }
      } else {
        control.setValue(this.emptyFor(option.componentType), { emitEvent: false });
      }

      control.updateValueAndValidity({ emitEvent: false });
    });

    this.calculateAndUpdateProgress();
  }


  calculateAndUpdateProgress(): void {
    if (!this.configurationForm) {
      this.updateProgress(60); // Base progress when model is selected
      return;
    }

    let totalFields = 0;
    let filledFields = 0;

    // Count all enabled and visible attributes
    this.filteredAttributes.forEach(attribute => {
      if (attribute.isEnable && !attribute.isHidden) {
        totalFields++;
        const control = this.configurationForm.get(attribute.attributeOptionId.toString());
        if (control && this.isFieldFilled(control.value)) {
          filledFields++;
        }
      }
    });

    // Count all enabled and visible options
    this.filteredOptions.forEach(option => {
      if (option.isEnable && !option.isHidden) {
        totalFields++;
        const control = this.configurationForm.get(option.attributeOptionId.toString());
        if (control && this.isFieldFilled(control.value)) {
          filledFields++;
        }
      }
    });

    // Define base and max additional progress
    const baseProgress = 60;
    const maxAdditionalProgress = 40;

    // Calculate additional progress based on filled fields
    const additionalProgress = totalFields > 0
      ? Math.round((filledFields / totalFields) * maxAdditionalProgress)
      : 0; // If no fields, no additional progress

    let progress = baseProgress + additionalProgress;

    // Ensure progress does not exceed 100%
    progress = Math.min(progress, 100);

    this.updateProgress(progress);
  }


  public getDefaultSelectValue(values: any): string {

    if (values.length > 0 &&
      values.every((val: any) => typeof val.value === 'number')) {
      return "0";
    }
    else {
      return "";
    }
  }

  groupModelsByGroup(models: any[]): { [key: string]: any[] } {
    return models.reduce((groups, model) => {
      const groupName = model.group;
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(model);
      return groups;
    }, {});
  }

  /**
   * Helper method to determine if a field is filled.
   * Adjust this method based on the type of your form controls.
   * @param value The value of the form control.
   * @returns A boolean indicating whether the field is filled.
   */
  private isFieldFilled(value: any): boolean {
  if (value == null) return false;

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number') {
    return value > -1;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
}
  
  updateProgress(progress: number): void {
    this.progressUpdated.emit(progress);
  }
}

